import React, { useState, useEffect } from 'react'
import OneSignal from 'react-onesignal'
import { Alert, AlertDescription, AlertTitle } from './ui/alert'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'

type NotificationPermission = 'default' | 'granted' | 'denied'

type BrowserType = 'Chrome' | 'Safari' | 'Firefox' | 'Edge' | 'Other'

const browserInstructions: Record<BrowserType, string> = {
  Chrome: 'Click the lock icon in the address bar → Site settings → Notifications → Allow',
  Safari: 'Safari → Settings → Websites → Notifications → Select this site → Allow',
  Firefox: 'Click the lock icon in the address bar → More Information → Permissions → Notifications → Allow',
  Edge: 'Click the lock icon in the address bar → Site permissions → Notifications → Allow',
  Other: 'Check your browser settings to enable notifications for this site'
}

export function NotificationPermissionBanner() {
  const [permission, setPermission] = useState<NotificationPermission>('granted')
  const [isDismissed, setIsDismissed] = useState(false)
  const [showInstructions, setShowInstructions] = useState(false)
  const [selectedBrowser, setSelectedBrowser] = useState<BrowserType>('Chrome')

  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission as NotificationPermission)
      
      // Check if user has dismissed the banner in the last 7 days
      const dismissedUntil = localStorage.getItem('notificationBannerDismissedUntil')
      if (dismissedUntil && new Date(dismissedUntil) > new Date()) {
        setIsDismissed(true)
      }
    }
  }, [])

  const handleEnableNotifications = async () => {
    try {
      const granted = await OneSignal.Notifications.requestPermission()
      if (granted) {
        setPermission('granted')
        
        // Auto-subscribe the user after permission is granted
        try {
          const isPushEnabled = await OneSignal.User.PushSubscription.optedIn
          if (!isPushEnabled) {
            console.log('Auto-subscribing user to push notifications from banner')
            await OneSignal.User.PushSubscription.optIn()
          }
        } catch (subscribeError) {
          console.error('Error subscribing to push notifications:', subscribeError)
        }
      } else {
        setShowInstructions(true)
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error)
      setShowInstructions(true)
    }
  }

  const handleDismiss = () => {
    setIsDismissed(true)
    // Dismiss for 7 days
    const dismissUntil = new Date()
    dismissUntil.setDate(dismissUntil.getDate() + 7)
    localStorage.setItem('notificationBannerDismissedUntil', dismissUntil.toISOString())
  }

  if (permission === 'granted' || isDismissed) {
    return null
  }

  const getAlertVariant = () => {
    if (permission === 'denied') return 'destructive'
    return 'info'
  }

  const getPermissionStatusText = () => {
    if (permission === 'denied') {
      return 'Notifications are currently disabled in your browser.'
    }
    return 'Enable notifications to receive important updates!'
  }

  return (
    <div className="w-full bg-lightblue py-4">
      <div className="container mx-auto">
        <Alert variant={getAlertVariant()} className="relative border-primary bg-white shadow-sm">
          <div className="flex items-start gap-4">
            {/* Bell Icon */}
            <svg
              className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
              />
            </svg>

            <div className="flex-1 space-y-3">
              <div>
                <AlertTitle className="text-base font-semibold text-secoundary">
                  {permission === 'denied' ? 'Notifications Disabled' : 'Enable Push Notifications'}
                </AlertTitle>
                <AlertDescription className="mt-1 text-sm text-secoundary">
                  {getPermissionStatusText()}
                </AlertDescription>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center gap-2">
                {permission === 'denied' ? (
                  <button
                    onClick={() => setShowInstructions(!showInstructions)}
                    className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-btnhover"
                  >
                    {showInstructions ? 'Hide' : 'View'} Browser Instructions
                  </button>
                ) : (
                  <button
                    onClick={handleEnableNotifications}
                    className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-btnhover"
                  >
                    Enable Now
                  </button>
                )}
                
                <button
                  onClick={handleDismiss}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-secoundary transition-colors hover:bg-gray-50"
                >
                  Dismiss
                </button>
              </div>

              {/* Browser Instructions */}
              {showInstructions && (
                <div className="space-y-3 rounded-md border border-gray-200 bg-gray-50 p-4">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-secoundary">Select your browser:</label>
                    <Select value={selectedBrowser} onValueChange={(value: BrowserType) => setSelectedBrowser(value)}>
                      <SelectTrigger className="w-[180px] bg-white">
                        <SelectValue placeholder="Select browser" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Chrome">Chrome</SelectItem>
                        <SelectItem value="Safari">Safari</SelectItem>
                        <SelectItem value="Firefox">Firefox</SelectItem>
                        <SelectItem value="Edge">Edge</SelectItem>
                        <SelectItem value="Other">Other Browser</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <Alert variant="warning" className="mt-2">
                    <AlertDescription className="text-sm">
                      <strong className="font-semibold">{selectedBrowser} Instructions:</strong>
                      <p className="mt-1">{browserInstructions[selectedBrowser]}</p>
                    </AlertDescription>
                  </Alert>
                </div>
              )}
            </div>

            {/* Close Button */}
            <button
              onClick={handleDismiss}
              className="flex-shrink-0 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              aria-label="Close"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </Alert>
      </div>
    </div>
  )
}
